/* eslint-disable no-underscore-dangle, prefer-destructuring */

'use client';

import {
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/navigation';
import useDeviceDetect from '@/helpers/useDeviceDetect';
import Icon from '@/core/icon';
import { lang, hocTranslateServerClient } from '@/helpers/translate';
import { useZusHomeStore } from '@/helpers/context';
import { initNoClientData, useSpacePublicZustandStore } from '@/helpers/spacepublicprovider';
import { useZusTrackerStore } from '@/helpers/tracker.js';
import Head from '@/core/head';

import {
  SpacePublicReviews,
  SpacePublicDescription,
  SpacePublicGoogle,
  SpacePublicSimilarSpaces,
  SpacePublicMobileCartridge,
  SpacePublicInternal,
  // SpacePublicDistrict,
  SpacePublicTopPictures,
  BreadcrumCms,
} from '@/core/dynamic';

import computeType from '@/helpers/space/computeType.js';

import SpacePublicCartridge from '@/components/space/public/cartridge';

import SpacePublicContent from '@/components/space/public/content.js';
import { ucfirst } from '@/helpers/common.js'; // isClient
import CalendarSelectableStore from '@/components/space/public/calendar/store';

function InitDateDataFromSearch({ spaceRef }) {
  useEffect(() => {
    CalendarSelectableStore.init(spaceRef);
    useSpacePublicZustandStore.setState({ datesSelected: { ...CalendarSelectableStore.getPeriod() } });
  }, []);
  return null;
}

function SpacePublicMetadata({ item, langCurrent, canonicalGetCategory }) {
  // Beaucoup de metatitle contiennent déjà la ville (« Loft avec studio de yoga - Paris ») :
  // ne l'accoler que si elle en est absente, sinon on affiche « - Paris - Paris » dans les
  // résultats Google et les cartes de partage.
  const metatitle = item?.seoByLang?.metatitle || '';
  const locality = item?.location?.locality || '';
  const cityAlreadyInTitle = locality && metatitle.toLowerCase().includes(locality.toLowerCase());
  const citySegment = cityAlreadyInTitle ? '' : ` - ${ucfirst(`${locality} `)}`;
  const titleContent = `${metatitle}${citySegment} -  ${item?.titleKeyword ? `${item.titleKeyword}` : ''}`;
  const mapUrls = {
    fr: process.env.NEXT_PUBLIC_URL_V3 + item.urlSpaceFR,
    en: process.env.NEXT_PUBLIC_URL_V3 + item.urlSpaceEN,
    es: process.env.NEXT_PUBLIC_URL_V3 + item.urlSpaceES,
  };
  // Open Graph : photo du lieu forcée en .jpg (les parseurs de cartes — WhatsApp,
  // Facebook, iMessage — ne lisent pas tous le webp). Largeur demandée 400 : l'imager
  // sert du retina ×2 (= 800 px réels, quality 100), soit ~300 Ko max mesuré — la
  // grande carte WhatsApp exige de rester sous ~600 Ko, et le 800 demandé (1600 px
  // réels) dépassait 1 Mo sur les photos denses ⇒ vignette au lieu de la bannière.
  const ogPicture = item?.pictures?.[1] || item?.pictures?.[0] || null;
  const ogImage = ogPicture
    ? `${process.env.NEXT_PUBLIC_IMAGER_ENGINE}/${ogPicture.nameId}-400-*-${ogPicture.extension}.jpg`
    : `${process.env.NEXT_PUBLIC_URL_V3}/img/og-default.jpg`;
  const ogTitle = `${metatitle || item?.title || ''}${cityAlreadyInTitle ? '' : ` - ${ucfirst(locality)}`}`;
  const ogLocales = { fr: 'fr_FR', en: 'en_US', es: 'es_ES' };
  // JSON-LD calqué sur le microdata Product déjà présent dans la page (article
  // itemScope Product : name, sku, image, aggregateRating où item.rate = la note /5).
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: item?.seoByLang?.metatitle || item?.title || undefined,
    description: item?.seoByLang?.metadescription || undefined,
    sku: item?.ref || undefined,
    url: mapUrls[langCurrent],
    image: (item?.pictures || []).map((pic) => `${process.env.NEXT_PUBLIC_IMAGER_ENGINE}/${pic.nameId}-800-*-${pic.extension}.jpg`),
    ...(item?.rate && item?.reviewCounter ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: item.rate,
        ratingCount: item.reviewCounter,
        bestRating: 5,
        worstRating: 1,
      },
    } : {}),
  };

  return (
    <>
      <title>{titleContent}</title>
      <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      <meta name="description" content={item?.seoByLang?.metadescription || ''} />
      <link rel="canonical" href={mapUrls[langCurrent]} />
      <link rel="alternate" hrefLang="x-default" href={mapUrls.fr + canonicalGetCategory()} />
      {['en', 'fr', 'es'].map((langlang) => (
        <link
          key={langlang}
          rel="alternate"
          hrefLang={langlang}
          href={mapUrls[langlang] + canonicalGetCategory()}
        />
      ))}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="We Are Scene" />
      <meta property="og:title" content={ogTitle} />
      {!item?.seoByLang?.metadescription || <meta property="og:description" content={item.seoByLang.metadescription} />}
      <meta property="og:url" content={mapUrls[langCurrent]} />
      <meta property="og:locale" content={ogLocales[langCurrent] || 'fr_FR'} />
      <meta property="og:image" content={ogImage} />
      {!item?.seoByLang?.image_alt || <meta property="og:image:alt" content={item.seoByLang.image_alt} />}
      <meta name="twitter:card" content="summary_large_image" />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
      />
    </>
  );
}

const SpacePublicChild = function SpacePublicChild({ item, t }) {
  const langCurrent = lang();
  const router = useRouter();
  const { ua, extWebpJpg: extension } = useZusHomeStore.getState();
  const { setOrEvent } = useZusTrackerStore.getState();
  const [isClient, setIsClient] = useState(false);
  const [capacitymax] = useState(Math.max(...Object.values(item?.dataByCategory?.capacities || { default: 1 })));
  const { isMobile } = useDeviceDetect();

  useEffect(() => {
    setIsClient(true);
    console.log(item?.ref);
  }, []);

  useEffect(() => {
    setOrEvent('view_item', item);
  }, [item]);

  function canonicalGetCategory() {
    if (typeof window !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      return params.get('category') ? `?category=${params.get('category')}` : '';
    }
    return '';
  }

  const formattedAddress = item?.location?.formatted_address;
  const zipCodeRegex = /\b\d{5}\b/;
  const zipCodeMatch = formattedAddress.match(zipCodeRegex);
  let zipCode = null;
  let cleanedAddress = formattedAddress;

  if (zipCodeMatch) {
    [zipCode] = zipCodeMatch;
    cleanedAddress = formattedAddress.replace(zipCodeRegex, '');
  }

  const cityCountryRegex = /,.*$/;
  cleanedAddress = cleanedAddress.replace(cityCountryRegex, '').trim().replace(/,+$/, '').trim();

  function normalizeUrl(url) {
    const baseUrl = url.split('?')[0];
    return baseUrl;
  }

  const newimageSeo = item?.pictures?.[1] || null;
  const imageSeo = item?.pictures?.[1]
    ? newimageSeo
    : false;

  return (
    <div className="min-h-screen bg-white">
      <article className="relative font-display" itemScope itemType="https://schema.org/Product">
        <InitDateDataFromSearch spaceRef={item.ref} />
        <section className="bg-white flex flex-col md:min-h-[380px] lg:min-h-[500px] transition overflow-hidden h-full w-full relative">
          <div
            className="absolute z-40 flex items-center justify-center bg-white rounded-lg lg:hidden h-9 w-9 top-4 left-4 shadow-button"
            onClick={() => {
              const previousUrl = normalizeUrl(document.referrer);
              const currentUrl = normalizeUrl(window.location.href);

              if (previousUrl !== currentUrl) {
                window.history.back();
              }
            }}
          >
            <Icon name="next" className="w-6 h-6 rotate-180" />
          </div>
          <SpacePublicTopPictures
            pictures={item?.pictures || []}
            spaceRef={item.ref}
            title={item.title}
            options={{
              forceGeneratePicture: item?.forceGeneratePicture,
            }}
          />
          <div className="relative pt-20 pb-6 -mb-24 text-was-ink md:pb-10 md:-mb-32 md:pt-28 bg-white border-b border-was-ink/8 -top-24 md:-top-32">
            <div className="container relative z-10 flex flex-col gap-2 px-6 pt-12 mx-auto md:px-10 lg:px-10 xl:px-20 2xl:px-48">
              <div className="flex flex-col md:flex-row justify-between w-full max-w-[700px] gap-4 md:gap-0">
                <div>
                  <h1
                    className="m-0 font-heading text-[20px] font-extrabold uppercase leading-[1.08] tracking-[-0.01em] text-was-ink text-balance md:text-[24px] lg:text-[28px]"
                    itemProp="name"
                  >
                    {item.title}
                  </h1>
                  <div className="flex mt-2 text-sm text-was-muted md:text-base">
                    <span>
                      {item?.location?.locality}
                      ,
                      {' '}
                      {item?.location?.country}
                    </span>
                    <span className="mx-2">•</span>
                    <span>{computeType({ type: (item?.type || 'flat'), t })}</span>
                  </div>
                  <div className="hidden" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                    <span itemProp="streetAddress">{cleanedAddress}</span>
                    <span itemProp="postalCode">{zipCode}</span>
                    <span itemProp="addressLocality">
                      {item?.location?.locality}
                      ,
                      {' '}
                      {item?.location?.country}
                    </span>
                  </div>
                  <meta itemProp="sku" content={item.ref} />
                </div>
                <div>
                  {!item.rate || (
                    <>
                      <div className="text-base md:text-lg">
                        <a href="#id_reviews" className="flex items-center">
                          <div className="size-4 md:w-[18px] md:h-[18px]">
                            <Icon
                              name="star"
                              className="fill-current text-purple size-4 md:w-[18px] md:h-[18px]"
                            />
                          </div>
                          <span className="mx-1 font-medium text-was-ink">
                            {item.rate}
                          </span>
                          <span className="px-2 py-0.5 ml-2 text-sm md:text-base font-medium transition bg-was-ink/5 rounded-md text-was-ink hover:bg-was-violet/10 hover:text-was-violet whitespace-nowrap">
                            {item?.reviewCounter <= 1
                              ? t('review:count_review', {
                                count: item?.reviewCounter || 0,
                              })
                              : t('review:count_review_plural', {
                                count: item?.reviewCounter || 0,
                              })}
                          </span>
                        </a>
                      </div>
                      <div
                        itemProp="aggregateRating"
                        itemScope
                        itemType="https://schema.org/AggregateRating"
                      >
                        <meta itemProp="ratingValue" content={item.rate} />
                        <meta itemProp="name" content={item.title} />
                        <meta
                          itemProp="ratingCount"
                          content={item?.reviewCounter || 0}
                        />
                        <meta itemProp="bestRating" content="5" />
                        <meta itemProp="worstRating" content="1" />
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="max-w-[740px] flex flex-wrap gap-4 md:gap-x-8 mt-4 md:mt-6">
                {!capacitymax || (
                <div className="flex flex-col justify-center">
                  <span className="text-xs md:text-sm text-was-muted">{t('space:title.capacity')}</span>
                  <span className="text-xs font-medium text-was-ink md:text-sm">{t('space:capacity_max', { capacity: capacitymax })}</span>
                </div>
                )}
                {!item?.surface || (
                <div className="flex flex-col justify-center">
                  <span className="text-xs md:text-sm text-was-muted">{t('form:label.surface')}</span>
                  <span className="text-xs font-medium text-was-ink md:text-sm">
                    {item?.surface}
                    {' '}
                    m²
                  </span>
                </div>
                )}
                {!item?.dataByCategory?.rooms || (
                <div className="flex flex-col justify-center">
                  <span className="text-xs md:text-sm text-was-muted">{t('space:title.room')}</span>
                  <span className="text-xs font-medium text-was-ink md:text-sm">{item?.dataByCategory?.rooms}</span>
                </div>
                )}
                {!item?.floor || (
                <div className="flex flex-col justify-center">
                  <span className="text-xs md:text-sm text-was-muted">{t('space:floor_title')}</span>
                  <span className="text-xs font-medium text-was-ink md:text-sm">{t(`space:floor.${item?.floor}`)}</span>
                </div>
                )}
                {!item?.dataByCategory?.highceiling || (
                <div className="flex flex-col justify-center">
                  <span className="text-xs md:text-sm text-was-muted">{t('space:highceiling')}</span>
                  <span className="text-xs font-medium text-was-ink md:text-sm">
                    {item?.dataByCategory?.highceiling}
                    m
                  </span>
                </div>
                )}
                <div className="ml-0 md:ml-4">
                  {!item?.plan?.downloadUrl || (
                    <button
                      className="flex items-center self-end px-3 py-2 md:px-3.5 md:py-2.5 transition rounded-lg text-white group bg-was-ink hover:bg-was-violet"
                      onClick={() => {
                        window.open(item?.plan?.downloadUrl, '_blank').focus();
                      }}
                    >
                      <div className="mr-2 size-3">
                        <Icon name="plan" className="text-white fill-current size-3 group-hover:text-white" />
                      </div>
                      <span className="text-xs font-medium text-white md:text-sm">Plan</span>
                    </button>
                  )}
                </div>
                {/* WEA-162 — badge "fiche technique dispo". Reteinte pour le fond blanc de
                    WEA-166 : le texte etait en blanc sur bg-green/20, donc illisible. */}
                {!item?.technicalSheet?.isPublic || (
                  <div className="flex items-center self-center px-3 py-1.5 rounded-lg bg-green/10 border border-green/30">
                    <span className="text-xs font-medium text-was-ink md:text-sm">
                      {t('space:badge.technicalSheet')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            {!imageSeo || (
              <meta
                itemProp="image"
                content={`${process.env.NEXT_PUBLIC_IMAGER_ENGINE}/${imageSeo.nameId}-800-*-${imageSeo.extension}.${extension}`}
              />
            )}
          </div>
        </section>
        <section className="relative bg-white">
          <div className="container flex flex-col px-6 mx-auto md:px-10 lg:px-10 xl:px-20 2xl:px-48 md:flex-row">
            <div className="relative w-full min-h-dvh lg:w-4/7 xl:w-5/9">
              <SpacePublicContent
                item={item}
                avatar={item.host}
                firstname={item?.host?.firstname || 'H'}
                categories={item.categories}
                spaceId={item.spaceId}
                spaceRef={item.ref}
                floor={item.floor}
                hostRef={item?.host?.ref}
                stations={item.stations}
                ambiences={item.ambiences}
                intern_rules={item.intern_rules}
                extrahour={item.extrahour}
                content={item.dataByCategory}
                dataByCategory={item.dataByCategory}
                covid_free={item.covid_free}
                isSuperhost={item.isSuperhost}
                contentDescription={<SpacePublicDescription {...{ item }} />}
                surface={item.surface}
                location={item.location}
              />
            </div>
            <div className="hidden lg:contents">
              {!isMobile && (
              <SpacePublicCartridge
                {...{
                  spaceRef: item.ref,
                  categories: item.categories,
                  hostRef: item?.host?.ref,
                  formules: item?.dataByCategory?.formules,
                  capacities: item?.dataByCategory?.capacities,
                  dataByCategory: item.dataByCategory,
                }}
              />
              )}
            </div>
          </div>
        </section>
        <section className="relative bg-white">
          <div className="container flex flex-col px-6 mx-auto md:px-10 lg:px-10 xl:px-20 2xl:px-48 md:flex-row">
            <div className="w-full">
              <SpacePublicReviews
                reviews={item.reviews}
                rate={item.rate}
                reviewCounter={item.reviewCounter}
                spaceRef={item.ref}
              />
            </div>
          </div>
        </section>
        <section className="relative bg-white">
          <div className="container flex flex-col px-6 py-12 mx-auto bg-white md:py-20 md:px-10 lg:px-10 xl:px-20 2xl:px-48 md:flex-row">
            <div className="w-full">
              <h2 className="w-full pt-4 pb-4 border-t md:pt-6 border-white-dark2 md:pt-12 lg:pb-4 m-0 font-heading text-[14px] font-extrabold uppercase leading-[1.2] tracking-[0.02em] text-was-ink md:text-[15px]">
                {t('space:location_map')}
              </h2>
              <div className="w-full h-[300px] md:h-[500px]">
                <SpacePublicGoogle location={item.location || null} />
              </div>
            </div>
          </div>
        </section>
        <section className="flex-row border-b bg-was-cream text-purple-5/50 border-white-dark3">
          <div className="container flex flex-col px-6 py-3 mx-auto md:py-4 md:px-10 lg:px-10 xl:px-20 2xl:px-48">
            <BreadcrumCms items={item.breadcrumb} />
          </div>
        </section>
        <SpacePublicSimilarSpaces
          similarSpacesDefault={item.spacesSimilar}
          data={null}
        />
        {isMobile && <SpacePublicMobileCartridge {...{ item }} isMobile />}
      </article>
    </div>
  );
};

const SpacePublicComponent = hocTranslateServerClient(({ t, ...props }) => {
  let { item } = props;
  if (!item) item = {};
  const date = CalendarSelectableStore.getPeriodInit(item.ref);
  // La locale de la route (prop serveur) : en SSR le store zustand `lang()` vaut
  // toujours 'fr' (jamais synchronisé côté client-components), ce qui rendait le
  // canonical FR sur les URLs EN/ES — lang() ne sert que de repli client.
  const langCurrent = props.locale || lang();

  initNoClientData({
    category: item.categoryDefault,
    spaceId: item.spaceId,
    spaceRef: item.ref,
    cartridgeOpenDates: Object.keys(date).length !== 0,
    spacePublicUrls: {
      fr: item.urlSpaceFR,
      en: item.urlSpaceEN,
      es: item.urlSpaceES,
    },
    publish: item.publish,
    // WEA-162: dispo fiche technique (lu par le cartouche pour la CTA "Demander")
    technicalSheetIsPublic: item?.technicalSheet?.isPublic,
  });

  function canonicalGetCategory() {
    if (typeof window !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      return params.get('category') ? `?category=${params.get('category')}` : '';
    }
    return '';
  }

  return (
    <>
      <SpacePublicMetadata
        item={item}
        langCurrent={langCurrent}
        canonicalGetCategory={canonicalGetCategory}
      />
      <SpacePublicChild {...{ item }} {...{ t }} />
    </>
  );
});

export {
  SpacePublicComponent,
};

export default SpacePublicComponent;
